import {asyncScheduler, MonoTypeOperatorFunction} from "rxjs";
import {throttleTime} from "rxjs/operators";

export interface TagInfo {
    tags: string[];
    label?: string;
}

/** Label of a message about a change spanning many domains: `transactions:*` or `domain:*`. */
export const ALL_DOMAINS_LABEL = '*';

/** Most often a page reloads in response to a stream of notifications. */
const RELOAD_THROTTLE_MS = 5000;

/** For pages that reload on messages any change can send (a long bulk run sends one per domain):
 * react to the first message at once, then at most once per {@link RELOAD_THROTTLE_MS} while more keep
 * coming, ending with one reload after the last.
 */
export function throttleReloads<T>(): MonoTypeOperatorFunction<T> {
    return throttleTime<T>(RELOAD_THROTTLE_MS, asyncScheduler, {leading: true, trailing: true});
}

/** Knows how to map push notification message strings to cache tags to be cleared upon receipt.
 */
export class CacheMessageMapper {

    static readonly USERS_PAT = /users/;
    static readonly DNS_ZONE_PAT = /dns-zone:(\S+)/;
    static readonly TRANSACTIONS_PAT = /transactions:(\S+)/;
    static readonly DOMAINS_PAT = /domain:(\S+)/;
    static readonly SETTINGS_PAT = /settings:(\S+)/;
    static readonly BULK_PAT = /^bulk$/;

    static map(message: string, pattern: RegExp): TagInfo | undefined {
        const m = message.match(pattern);
        if (m) {
            if (pattern === CacheMessageMapper.USERS_PAT) {
                return {
                    tags: ['users']
                };
            } else if (pattern === CacheMessageMapper.DNS_ZONE_PAT) {
                const zone = m[1];
                return {
                    tags: [`dns-zone-domains:${zone}`],
                    label: zone
                };
            } else if (pattern == CacheMessageMapper.TRANSACTIONS_PAT) {
                return {
                    tags: [
                        'transactions.last_n',
                        'transactions.last_30_days',
                        'transactions.last_60_days',
                        'status_summary',
                        // The admin page's user list carries each user's transaction count.
                        'users'
                    ],
                    label: m[1] // domain name, or '*' for a change spanning many domains
                }
            } else if (pattern == CacheMessageMapper.BULK_PAT) {
                // A task group was queued, started or finished. Its transactions arrive as
                // their own messages; this one also drops the cached user list (and its
                // transaction counts), so the admin page shows the group's effect.
                return {
                    tags: ['users']
                }
            } else if (pattern == CacheMessageMapper.DOMAINS_PAT) {
                return  {
                    tags: [],
                    label: m[1] // domain name, or '*' for a change spanning many domains
                }
            } else if (pattern == CacheMessageMapper.SETTINGS_PAT) {
                return {
                    tags: ['settings'],
                    label: m[1] // user ID
                }
            }
        }
        return undefined;
    }
}
